import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { getDeviceId } from '../utils/deviceId';
import { getStorageJSON, setStorageJSON, removeStorageItem } from '../utils/capacitorStorage';
import offlineAuthService from '../services/offlineAuthService';
import { getNetworkStatus } from '../utils/capacitorNetwork';
import geofenceService from '../services/geofenceService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [operator, setOperator] = useState(null);
  const [auth, setAuth] = useState(null);
  const [geofenceStatus, setGeofenceStatus] = useState(null);
  const [geofenceLoading, setGeofenceLoading] = useState(false);
  const [geofenceError, setGeofenceError] = useState(null);

  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        // Try Capacitor storage first, fallback to localStorage
        let stored = await getStorageJSON('operatorAuth');
        let storedOp = await getStorageJSON('operatorData');
        
        // Fallback to localStorage for backward compatibility
        if (!stored) {
          const localStorageValue = localStorage.getItem('operatorAuth');
          if (localStorageValue) {
            stored = JSON.parse(localStorageValue);
          }
        }
        if (!storedOp) {
          const localStorageValue = localStorage.getItem('operatorData');
          if (localStorageValue) {
            storedOp = JSON.parse(localStorageValue);
          }
        }

        if (stored) {
          setAuth(stored);
          if (storedOp) {
            setOperator(storedOp);
          }
          // attempt to re-validate in background (only if online)
          const fetchOp = async () => {
            try {
              // Check if online
              let isOnline = true;
              try {
                const networkStatus = await getNetworkStatus();
                isOnline = networkStatus.connected;
              } catch (e) {
                isOnline = navigator.onLine;
              }

              // If offline, use cached operator data
              if (!isOnline && storedOp) {
                console.log('[AuthContext] Offline - using cached operator data');
                setOperator(storedOp);
                return;
              }

              // Online: re-validate with server
              // Get device identifier for re-validation
              const deviceId = await getDeviceId();
              
              const res = await api.post('/login/', {
                pin: stored.pin,
                operator_id: stored.operator_id,
                mac_address: deviceId.mac_address || undefined,
                device_fingerprint: deviceId.device_fingerprint || undefined,
              });
              setOperator(res.data.operator);
              
              // Update cache with fresh data
              await offlineAuthService.cacheOperatorCredentials(res.data.operator, stored.pin, deviceId);
              
              // Save to both Capacitor storage and localStorage
              await setStorageJSON('operatorData', res.data.operator);
              localStorage.setItem('operatorData', JSON.stringify(res.data.operator));
            } catch (e) {
              // Only clear auth if it's a real authentication error, not a network error
              if (e.response && (e.response.status === 401 || e.response.status === 403)) {
                await removeStorageItem('operatorAuth');
                await removeStorageItem('operatorData');
                localStorage.removeItem('operatorAuth');
                localStorage.removeItem('operatorData');
                setAuth(null);
                setOperator(null);
              } else if (!e.response) {
                // Network error - keep cached data for offline use
                console.log('[AuthContext] Network error - keeping cached auth for offline use');
                if (storedOp) {
                  setOperator(storedOp);
                }
              }
              // For other errors, keep the stored auth and let the user continue
              // The stored auth will work for API calls via interceptors
            }
          };
          fetchOp();
        }
      } catch (e) {
        console.error('Error loading stored auth:', e);
      }
    };
    
    loadStoredAuth();
  }, []);

  const handleLogin = (op, authObj) => {
    setOperator(op);
    setAuth(authObj);
  };

  const logout = useCallback(async () => {
    try {
      await removeStorageItem('operatorAuth');
      await removeStorageItem('operatorData');
    } catch (e) {
      // ignore
    }
    try {
      localStorage.removeItem('operatorAuth');
      localStorage.removeItem('operatorData');
      localStorage.removeItem('selectedRole');
    } catch (e) {
      // ignore
    }
    setAuth(null);
    setOperator(null);
    setGeofenceStatus(null);
    setGeofenceError(null);
    setGeofenceLoading(false);
  }, []);

  const refreshGeofence = useCallback(async (forceFresh = false) => {
    if (!auth || !operator) return;
    setGeofenceLoading(true);
    setGeofenceError(null);
    try {
      const result = await geofenceService.checkLocationInGeofence(forceFresh);
      setGeofenceStatus(result);
    } catch (e) {
      setGeofenceStatus(null);
      setGeofenceError(e?.message || 'Geofence check failed');
    } finally {
      setGeofenceLoading(false);
    }
  }, [auth, operator]);

  useEffect(() => {
    if (!auth || !operator) return;
    refreshGeofence(false);
  }, [auth, operator, refreshGeofence]);

  useEffect(() => {
    if (!auth || !operator) return;
    const interval = setInterval(() => {
      refreshGeofence(false);
    }, 300000);
    return () => clearInterval(interval);
  }, [auth, operator, refreshGeofence]);

  return (
    <AuthContext.Provider
      value={{
        operator,
        auth,
        handleLogin,
        logout,
        geofenceStatus,
        geofenceLoading,
        geofenceError,
        refreshGeofence,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};





